import { createEmptyChecklist, createEmptyProfile } from '../core/analyze';
import { AUDIENCE_MODES, type AudienceMode, type ProfileInput } from '../core/types';

/**
 * Turning untrusted JSON into a `ProfileInput`.
 *
 * Two paths feed profiles in from outside the app — a restored localStorage
 * blob and a shared URL — and both can carry anything at all: an older schema, a
 * hand-edited link, or a truncated string. Everything crossing that boundary
 * goes through `sanitiseProfile`, which is total: it never throws and always
 * returns a structurally valid profile, dropping whatever it cannot use.
 *
 * The alternative — trusting the shape and letting a rule crash on
 * `profile.skills.map` — would take the whole report down over a stale link.
 */

/** Guards against a pathological share link exhausting memory or the engine. */
const CAPS = {
  headline: 400,
  about: 5000,
  description: 4000,
  field: 200,
  skills: 100,
  experience: 30,
  recommendations: 999,
} as const;

function str(value: unknown, cap: number): string {
  return typeof value === 'string' ? value.slice(0, cap) : '';
}

function bool(value: unknown): boolean {
  return value === true;
}

function count(value: unknown, cap: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 0;
  return Math.min(cap, Math.max(0, Math.floor(value)));
}

function isMode(value: unknown): value is AudienceMode {
  return typeof value === 'string' && (AUDIENCE_MODES as readonly string[]).includes(value);
}

/** Coerces `value` to a valid mode, falling back to `student`. */
export function sanitiseMode(value: unknown): AudienceMode {
  return isMode(value) ? value : 'student';
}

export function sanitiseProfile(value: unknown): ProfileInput {
  if (typeof value !== 'object' || value === null) return createEmptyProfile();
  const raw = value as Record<string, unknown>;
  const rawChecklist =
    typeof raw.checklist === 'object' && raw.checklist !== null
      ? (raw.checklist as Record<string, unknown>)
      : {};

  return {
    headline: str(raw.headline, CAPS.headline),
    about: str(raw.about, CAPS.about),
    experience: Array.isArray(raw.experience)
      ? raw.experience.slice(0, CAPS.experience).map((entry, index) => {
          const e =
            typeof entry === 'object' && entry !== null ? (entry as Record<string, unknown>) : {};
          return {
            // Ids must be unique for React keys and for the form's edit
            // handlers; a shared link with duplicate ids would make two entries
            // edit as one, so the index is the fallback.
            id: str(e.id, CAPS.field) || `entry-${index}`,
            title: str(e.title, CAPS.field),
            organisation: str(e.organisation, CAPS.field),
            description: str(e.description, CAPS.description),
          };
        })
      : [],
    skills: Array.isArray(raw.skills)
      ? raw.skills
          .slice(0, CAPS.skills)
          .map((skill) => str(skill, CAPS.field))
          .filter(Boolean)
      : [],
    targetRole: str(raw.targetRole, CAPS.field),
    checklist: {
      ...createEmptyChecklist(),
      hasPhoto: bool(rawChecklist.hasPhoto),
      hasBanner: bool(rawChecklist.hasBanner),
      hasCustomUrl: bool(rawChecklist.hasCustomUrl),
      hasLocation: bool(rawChecklist.hasLocation),
      hasFeatured: bool(rawChecklist.hasFeatured),
      recommendationCount: count(rawChecklist.recommendationCount, CAPS.recommendations),
      openToWork: bool(rawChecklist.openToWork),
      hiring: bool(rawChecklist.hiring),
    },
  };
}

/** Deduplicates experience ids, which `sanitiseProfile` cannot do per-entry. */
export function withUniqueIds(profile: ProfileInput): ProfileInput {
  const seen = new Set<string>();
  return {
    ...profile,
    experience: profile.experience.map((entry, index) => {
      if (!seen.has(entry.id)) {
        seen.add(entry.id);
        return entry;
      }
      return { ...entry, id: `${entry.id}-${index}` };
    }),
  };
}
